import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FlowControlService } from '../tokens/flow-control.token';
import { FlowControlSettingDTO } from '../models/kyc.model';
import { API_PATHS } from '../config/api-paths';
import { extractBackendErrorMessage } from '../util/backend-error';

/**
 * REST-only implementation of `FlowControlService`.
 *
 * Calls the flow-control API:
 *   GET  /services/embedded/api/flow-control                  → FlowControlSettingDTO[]
 *   PUT  /services/embedded/api/flow-control/{channel}/toggle → FlowControlSettingDTO
 *   PUT  /services/embedded/api/flow-control/reset-all        → FlowControlSettingDTO[]
 *
 * Rule 07 / Rule 10 — no mock fallback. Playwright tests intercept via `page.route()`.
 * Rule 09 — mutating methods surface backend error messages via extractBackendErrorMessage.
 *
 * Authority required: ROLE_MASTER_ADMIN (enforced server-side and by roleGuard on the route).
 */
@Injectable()
export class FlowControlRestService implements FlowControlService {
  private readonly http = inject(HttpClient);

  private get base(): string {
    return API_PATHS.FLOW_CONTROL;
  }

  list(): Observable<FlowControlSettingDTO[]> {
    return this.http.get<FlowControlSettingDTO[]>(this.base);
  }

  toggle(channel: string, flowAEnabled: boolean, reason: string): Observable<FlowControlSettingDTO> {
    const body = { channel, flowAEnabled, reason };
    return this.http.put<FlowControlSettingDTO>(`${this.base}/${channel}/toggle`, body).pipe(
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.flowControl.toggleFailed');
        return throwError(() => ({ code: msg }));
      }),
    );
  }

  resetAll(): Observable<FlowControlSettingDTO[]> {
    return this.http.put<FlowControlSettingDTO[]>(`${this.base}/reset-all`, null).pipe(
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.flowControl.resetFailed');
        return throwError(() => ({ code: msg }));
      }),
    );
  }
}
